// Global JavaScript - Panel Centre Theme

// Trap focus within element (for modals/drawers)
function trapFocus(container) {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  container.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
    if (e.key === 'Escape') {
      container.dispatchEvent(new CustomEvent('close'));
    }
  });
}

// Debounce function
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Fetch configuration
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

// Serialize form data
function serializeForm(form) {
  const formData = new FormData(form);
  const object = {};
  formData.forEach((value, key) => {
    object[key] = value;
  });
  return JSON.stringify(object);
}

// Cart helpers used by AJAX add-to-cart controls.
async function getCart() {
  const response = await fetch(`${window.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) throw new Error(`Unable to refresh cart: ${response.status}`);
  return response.json();
}

function updateHeaderCartCount(cart) {
  document.querySelectorAll('.header__cart-count').forEach(element => {
    element.textContent = cart.item_count;
  });
}

async function refreshCartDrawer() {
  const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });

  if (!response.ok) throw new Error(`Unable to refresh cart drawer: ${response.status}`);

  const html = await response.text();
  const nextDocument = new DOMParser().parseFromString(html, 'text/html');
  const currentDrawer = document.querySelector('cart-drawer');
  const nextDrawer = nextDocument.querySelector('cart-drawer');

  if (!currentDrawer || !nextDrawer) throw new Error('Cart drawer markup not found');
  currentDrawer.replaceWith(nextDrawer);
}

// Product Form Component
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form') || this;
    this.submitButton = this.querySelector('[type="submit"]');

    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitButton.disabled) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');

    const formData = new FormData(this.form);
    const config = {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    };

    // Convert to JSON for sections API
    const body = {
      items: [
        {
          id: parseInt(formData.get('id')),
          quantity: parseInt(formData.get('quantity')) || 1
        }
      ]
    };

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...config,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        // Update cart count
        this.updateCartCount(data);
        // Open cart drawer
        document.dispatchEvent(new CustomEvent('cart:open'));
      } else {
        alert(data.description || 'Error adding to cart');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error adding to cart');
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('loading');
    }
  }

  updateCartCount(data) {
    const cartCountElements = document.querySelectorAll('.header__cart-count');
    if (data.items) {
      const totalItems = data.items.reduce((sum, item) => sum + item.quantity, 0);
      cartCountElements.forEach(el => el.textContent = totalItems);
    }
  }
}

customElements.define('product-form', ProductForm);

// Quantity Input Component
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.decreaseBtn = this.querySelector('[data-action="decrease"]');
    this.increaseBtn = this.querySelector('[data-action="increase"]');

    this.decreaseBtn?.addEventListener('click', () => this.changeQuantity(-1));
    this.increaseBtn?.addEventListener('click', () => this.changeQuantity(1));
    this.input?.addEventListener('change', () => this.validateInput());
  }

  changeQuantity(change) {
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || 9999;
    let value = parseInt(this.input.value) + change;

    value = Math.max(min, Math.min(max, value));
    this.input.value = value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  validateInput() {
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || 9999;
    let value = parseInt(this.input.value) || min;

    value = Math.max(min, Math.min(max, value));
    this.input.value = value;
  }
}

customElements.define('quantity-input', QuantityInput);

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function () {
  // Lazy load images
  if ('loading' in HTMLImageElement.prototype) {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    lazyImages.forEach(img => {
      img.src = img.src;
    });
  }

  // Product-card Quick Add. Delegation also covers cards appended by Load More.
  document.addEventListener('click', async event => {
    const button = event.target.closest('button.product-card__quick-add[data-variant-id]');
    if (!button || button.disabled) return;

    event.preventDefault();

    const originalText = button.textContent;
    const variantId = Number(button.dataset.variantId);

    if (!Number.isInteger(variantId) || variantId <= 0) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding...';

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add item');

      button.textContent = 'Added';

      try {
        const cart = await getCart();
        updateHeaderCartCount(cart);
      } catch (error) {
        console.warn(error);
      }

      try {
        await refreshCartDrawer();
      } catch (error) {
        console.warn(error);
      }

      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      console.error('Quick Add error:', error);
      button.textContent = 'Try Again';
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
        button.textContent = originalText;
      }, 1500);
    }
  });

  // Fallback for cached collection pages where the Load More button has no inline handler yet.
  const loadMoreButton = document.querySelector('.collection-products__load-more .btn:not([data-load-more-products])');
  const productGrid = document.querySelector('.collection-products__grid');

  if (loadMoreButton && productGrid) {
    const getNextUrl = doc => {
      const nextButton = doc.querySelector('[data-load-more-products]');
      if (nextButton?.dataset.nextUrl) return nextButton.dataset.nextUrl;

      const nextPaginationLink = doc.querySelector('.collection-products__pagination .next a');
      if (nextPaginationLink?.getAttribute('href')) return nextPaginationLink.getAttribute('href');

      const currentUrl = new URL(window.location.href);
      const currentPage = Number(currentUrl.searchParams.get('page') || 1);
      currentUrl.searchParams.set('page', String(currentPage + 1));
      return currentUrl.pathname + currentUrl.search;
    };

    loadMoreButton.dataset.nextUrl = getNextUrl(document);

    loadMoreButton.addEventListener('click', function () {
      const button = this;
      const nextUrl = button.dataset.nextUrl;

      if (!nextUrl || button.disabled) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Loading...';

      fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(response => {
          if (!response.ok) throw new Error('Unable to load more products');
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const nextDocument = parser.parseFromString(html, 'text/html');
          const nextGrid = nextDocument.querySelector('.collection-products__grid');
          const nextUrlAfterLoad = getNextUrl(nextDocument);
          const nextProductCount = nextGrid ? nextGrid.querySelectorAll('.product-card').length : 0;

          if (!nextGrid || nextProductCount === 0) throw new Error('Product grid not found');

          nextGrid.querySelectorAll('.product-card').forEach(card => {
            productGrid.appendChild(document.importNode(card, true));
          });

          const currentPagination = document.querySelector('.collection-products__pagination');
          const nextPagination = nextDocument.querySelector('.collection-products__pagination');
          if (currentPagination && nextPagination) {
            currentPagination.innerHTML = nextPagination.innerHTML;
          } else if (currentPagination && !nextPagination) {
            currentPagination.remove();
          }

          if (nextUrlAfterLoad && nextDocument.querySelector('.collection-products__pagination .next a, [data-load-more-products]')) {
            button.dataset.nextUrl = nextUrlAfterLoad;
            button.disabled = false;
            button.textContent = originalText;
          } else {
            button.closest('.collection-products__load-more')?.remove();
          }
        })
        .catch(error => {
          console.error(error);
          button.disabled = false;
          button.textContent = 'Try Again';
        });
    });
  }
});
